import isEqual from "lodash/isEqual";
import { intervalToDuration } from "date-fns";
import { NewRequisitionForm } from "../../recoil/types";
import { convertNewReqFormToRequest } from "./Services/helpers";
import {
  EXPANDED_CARRIER_PANEL,
  EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS,
  ExpandedCarrierPanel,
  NewRequisitionType,
} from "./types";

export type ChangedEditOrderPayload = Partial<NewRequisitionType>;

export type EditOrderResult =
  | { ok: true; status: number }
  | { ok: false; status: number; reason?: string; error?: string };

// The server sets these from the selected clinic and the stored order.
const SERVER_DERIVED_EDIT_FIELDS = new Set<keyof NewRequisitionType>([
  "clinic_name",
  "clinic_phone",
  "clinic_fax",
  "is_standard_account",
  "is_international_account",
  "is_mobile_phlebotomy_account",
  "is_patient_sheet_account",
  "is_insurance_sheet_account",
  "is_fetal_antigen_account",
  "is_fragile_x_account",
  "is_rhd_only_account",
  "is_130_panel_account",
  "is_611_panel_account",
  "is_113_custom_panel_account",
  "treatment_notes",
]);

export const buildChangedEditOrderPayload = (
  currentForm: NewRequisitionForm,
  originalForm: NewRequisitionForm,
): ChangedEditOrderPayload => {
  const currentPayload = convertNewReqFormToRequest(currentForm);
  const originalPayload = convertNewReqFormToRequest(originalForm);

  return Object.fromEntries(
    Object.entries(currentPayload).filter(
      ([field, value]) =>
        !SERVER_DERIVED_EDIT_FIELDS.has(field as keyof NewRequisitionType) &&
        !isEqual(value, originalPayload[field as keyof NewRequisitionType]),
    ),
  );
};

const CARRIER_PANEL_NAMES: Record<ExpandedCarrierPanel, string> = {
  [EXPANDED_CARRIER_PANEL.CARRIER_113_CUSTOM]: "the 113-gene custom panel",
  [EXPANDED_CARRIER_PANEL.CARRIER_130]: "the 130-gene panel",
  [EXPANDED_CARRIER_PANEL.CARRIER_611]: "the 611-gene panel",
};

export const getClinicChangeBlocker = (
  order: NewRequisitionForm,
  clinic: Partial<NewRequisitionForm>,
): string | null => {
  if (clinic.clinicAccountNumber === order.clinicAccountNumber) return null;
  if (!clinic.isInternationalAccount !== !order.isInternationalAccount) {
    return clinic.isInternationalAccount ? "International clinic" : "US clinic";
  }
  const panel = order.expandedCarrierPanel;
  const missing = [
    order.isRhdOnlyOrder && !clinic.isRhdOnlyAccount && "RhD-only testing",
    order.isFetalAntigen &&
      !clinic.isFetalAntigenAccount &&
      "fetal antigen testing",
    order.isFragileX && !clinic.isFragileXAccount && "Fragile X",
    order.isMobilePhlebotomy &&
      !clinic.isMobilePhlebotomyAccount &&
      "mobile phlebotomy",
    panel &&
      !clinic[EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS[panel]] &&
      CARRIER_PANEL_NAMES[panel],
  ].filter(Boolean);
  return missing.length ? `Doesn't offer ${missing.join(" or ")}` : null;
};

export const formatEditWindowTimeRemaining = (
  expiresAt: string,
  now: Date = new Date(),
): string => {
  const remainingMs = new Date(expiresAt).getTime() - now.getTime();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "0min";
  const totalMinutes = Math.ceil(remainingMs / 60000);
  const duration = intervalToDuration({
    start: now,
    end: new Date(now.getTime() + totalMinutes * 60000),
  });
  const hours = duration.hours ?? 0;
  const minutes = duration.minutes ?? 0;
  if (hours > 0) return `${hours}h and ${minutes}min`;
  return `${minutes}min`;
};

export type EditTooltipState = "editable" | "closed" | "urgent";

export const getEditTooltipState = (
  order: Pick<NewRequisitionType, "is_editable" | "edit_window_expires_at">,
  editFlagEnabled: boolean,
  now: Date = new Date(),
): EditTooltipState => {
  if (!editFlagEnabled || !order.is_editable) return "closed";
  if (!order.edit_window_expires_at) return "editable";

  const expiresAt = new Date(order.edit_window_expires_at).getTime();
  if (Number.isNaN(expiresAt)) return "closed";

  const minutesRemaining = (expiresAt - now.getTime()) / 60000;
  if (minutesRemaining <= 0) return "closed";
  return minutesRemaining < 10 ? "urgent" : "editable";
};

export const EDIT_TOOLTIP_COPY: Record<EditTooltipState, string> = {
  editable: "Edit this order",
  closed: "This order can no longer be edited",
  urgent: "You have less than 10 minutes to edit this order",
};

const DEFAULT_EDIT_ORDER_ERROR =
  "There was an error saving the edit. If the error persists, please contact support at (650)-460-2551.";

export const EDIT_ORDER_ERROR_COPY: Record<string, string> = {
  already_assigned:
    "This order can no longer be edited because it has already been assigned to a sample.",
  cfc_order: "This order can no longer be edited.",
  not_submitted: "This order has not been submitted and cannot be edited here.",
  replaced: "This order was already edited. Refresh to see the latest version.",
  sync_pending:
    "This order is still syncing. Please wait a few minutes and try again.",
  window_closed:
    "This order can no longer be edited because the edit window closed.",
};

export const getEditOrderErrorMessage = (result: EditOrderResult): string => {
  if (result.ok) return "";
  if (result.reason && EDIT_ORDER_ERROR_COPY[result.reason]) {
    return EDIT_ORDER_ERROR_COPY[result.reason];
  }
  if (result.status === 403) {
    return "Order editing is no longer available. Refresh to see the latest order status.";
  }
  if (result.status === 400) {
    return "Some order details are missing or invalid. Review the order and try again.";
  }
  return DEFAULT_EDIT_ORDER_ERROR;
};
